import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Pagination,
  FormControl,
  MenuItem,
  Select,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Tooltip,
} from "@mui/material";
import {
  Search,
  Add,
  Edit,
  Delete,
  Visibility,
  LocalHospital,
  Bed,
  Person,
  CalendarToday,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../../context";
import { listICUAdmissions, deleteICUAdmission } from "../../../../api/icu";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import AdmissionsTableSkeleton from "../../../../Components/GeneralComponents/admissionsTableSkeleton/AdmissionsTableSkeleton";

const AdmissionsList = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token || "";
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // State
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedAdmission, setSelectedAdmission] = useState(null);

  // Debounce search term to avoid excessive API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(1); // Reset to first page on new search
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch admissions
  const {
    data: admissionsData,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ["icu-admissions", page, rowsPerPage, debouncedSearch, statusFilter],
    queryFn: () =>
      listICUAdmissions(
        {
          page,
          page_size: rowsPerPage,
          search: debouncedSearch || undefined,
          status: statusFilter !== "all" ? statusFilter : undefined,
        },
        token
      ),
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id) => deleteICUAdmission(id, token),
    onSuccess: () => {
      queryClient.invalidateQueries(["icu-admissions"]);
      setDeleteDialogOpen(false);
      setSelectedAdmission(null);
    },
  });

  const handleDelete = (admission) => {
    setSelectedAdmission(admission);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (selectedAdmission) {
      deleteMutation.mutate(selectedAdmission.id);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 1:
        return "success";
      case 2:
        return "info";
      case 3:
        return "warning";
      case 4:
        return "error";
      default:
        return "default";
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 1:
        return t("icu.admissions.status.admitted", "Admitted");
      case 2:
        return t("icu.admissions.status.discharged", "Discharged");
      case 3:
        return t("icu.admissions.status.transferred", "Transferred");
      case 4:
        return t("icu.admissions.status.absent", "Absent Without Leave");
      default:
        return t("icu.admissions.status.unknown", "Unknown");
    }
  };

  const getAdmissionTypeText = (type) => {
    switch (type) {
      case 1:
        return t("icu.admissions.type.emergency", "Emergency");
      case 2:
        return t("icu.admissions.type.post_operative", "Post-Operative");
      case 3:
        return t("icu.admissions.type.medical", "Medical");
      case 4:
        return t("icu.admissions.type.transfer_from_ipd", "Transfer from IPD");
      default:
        return t("icu.admissions.type.unknown", "Unknown");
    }
  };

  const admissions = Array.isArray(admissionsData)
    ? admissionsData
    : admissionsData?.results || [];

  const totalCount = Array.isArray(admissionsData)
    ? admissions.length
    : admissionsData?.count || 0;

  if (isLoading) {
    return <AdmissionsTableSkeleton columns={7} rows={10} />;
  }

  if (isError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          {t("icu.admissions.error_loading", "Error loading admissions")}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h4" component="h1">
          {t("icu.admissions.title", "ICU Admissions")}
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<Add />}
          onClick={() => navigate("/icu/admissions/new")}
        >
          {t("icu.admissions.add_admission", "New Admission")}
        </Button>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box display="flex" gap={2} alignItems="center">
          <TextField
            placeholder={t("icu.admissions.search_placeholder", "Search by patient name, phone, ticket number, or bed number")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
            sx={{ flexGrow: 1 }}
          />
          <FormControl sx={{ minWidth: 150 }}>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              displayEmpty
            >
              <MenuItem value="all">{t("icu.admissions.filter.all", "All Status")}</MenuItem>
              <MenuItem value={1}>{t("icu.admissions.status.admitted", "Admitted")}</MenuItem>
              <MenuItem value={2}>{t("icu.admissions.status.discharged", "Discharged")}</MenuItem>
              <MenuItem value={3}>{t("icu.admissions.status.transferred", "Transferred")}</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead sx={{ backgroundColor: "grey.100" }}>
            <TableRow>
              <TableCell sx={{ fontWeight: "bold" }}>
                {t("icu.admissions.patient", "Patient")}
              </TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>
                {t("icu.admissions.bed", "Bed")}
              </TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>
                {t("icu.admissions.admission_date", "Admission Date")}
              </TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>
                {t("icu.admissions.type", "Type")}
              </TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>
                {t("icu.admissions.status", "Status")}
              </TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>
                {t("icu.admissions.diagnosis", "Diagnosis")}
              </TableCell>
              <TableCell sx={{ fontWeight: "bold" }} align="right">
                {t("icu.admissions.actions", "Actions")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {admissions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t("icu.admissions.no_admissions", "No admissions found")}
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<Add />}
                    onClick={() => navigate("/icu/admissions/new")}
                    sx={{ mt: 2 }}
                  >
                    {t("icu.admissions.add_admission", "New Admission")}
                  </Button>
                </TableCell>
              </TableRow>
            ) : (
              admissions.map((admission) => (
                <TableRow key={admission.id} hover>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Person color="primary" fontSize="small" />
                      <Typography variant="body2">
                        {admission.patient_name || "-"}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Bed color="primary" fontSize="small" />
                      <Typography variant="body2">
                        {admission.bed_info || "-"}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {admission.admission_date
                      ? new Date(admission.admission_date).toLocaleDateString()
                      : "-"}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getAdmissionTypeText(admission.admission_type)}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getStatusText(admission.status)}
                      color={getStatusColor(admission.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{
                        maxWidth: 300,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {admission.admission_diagnosis || "-"}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title={t("icu.admissions.view", "View")}>
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/icu/admissions/${admission.id}`)}
                      >
                        <Visibility fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {admission.status === 1 && (
                      <Tooltip title={t("icu.admissions.edit", "Edit")}>
                        <IconButton
                          size="small"
                          onClick={() =>
                            navigate(`/icu/admissions/${admission.id}/edit`)
                          }
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title={t("icu.admissions.delete", "Delete")}>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDelete(admission)}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mt={3}
        flexWrap="wrap"
        gap={2}
      >
        {/* Rows Per Page Selector */}
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="body2">
            {t("icu.admissions.rows_per_page", "Rows Per Page")}:
          </Typography>
          <FormControl variant="standard" sx={{ minWidth: 80 }}>
            <Select
              value={rowsPerPage}
              onChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(1);
              }}
            >
              <MenuItem value={5}>5</MenuItem>
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={25}>25</MenuItem>
              <MenuItem value={50}>50</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Pagination Component */}
        {totalCount > rowsPerPage && (
          <Pagination
            count={Math.ceil(totalCount / rowsPerPage)}
            page={page}
            onChange={(event, value) => setPage(value)}
            color="primary"
          />
        )}
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>
          {t("icu.admissions.delete_confirm_title", "Delete Admission")}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {t(
              "icu.admissions.delete_confirm_message",
              "Are you sure you want to delete this admission? This action cannot be undone."
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            {t("common.cancel", "Cancel")}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={confirmDelete}
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending
              ? t("common.deleting", "Deleting...")
              : t("common.delete", "Delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdmissionsList;

